import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  FaBuilding,
  FaCheck,
  FaEnvelope,
  FaGlobe,
  FaMapMarkerAlt,
  FaPhone,
  FaUser,
  FaUserShield,
} from 'react-icons/fa';
import PrimaryButtonComponent from '../../../Components/Buttons/PrimaryButton/PrimaryButton.component';
import { httpCreateCompany } from '../../../Hooks/company/company.post.hooks';

import {
  ReviewContainer,
  ReviewHeader,
  ReviewHeaderIcon,
  ReviewHeaderContent,
  ReviewIntro,
  ReviewSections,
  ReviewSection,
  ReviewSectionHeader,
  ReviewSectionTitle,
  ReviewEditButton,
  ReviewDetails,
  ReviewDetail,
  ReviewDetailIcon,
  ReviewDetailContent,
  ReviewEmpty,
  ReviewConfirmation,
  ReviewConfirmationIcon,
  ReviewConfirmationContent,
  ReviewError,
  ReviewActions,
  ReviewBackButton,
} from './SignUpSubmit.styles';

const CompanySignupReview = ({
  company,
  user,
  billingAddress,
  setStep,
  setResult,
  setIsOpen,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const formatNum = (num) => {
    // Remove all non-digit characters from the input
    const cleaned = ('' + num).replace(/\D/g, '');

    // Check if the cleaned number has 10 digits
    if (cleaned.length === 10) {
      // Format the number as (123) 456-7890
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(
        6
      )}`;
    }

    // If the number doesn't have 10 digits, return it as is
    return num;
  };

  const hasAddress =
    billingAddress?.street?.trim() ||
    billingAddress?.city?.trim() ||
    billingAddress?.province?.trim() ||
    billingAddress?.postalCode?.trim();

  const fullManagerName = [user?.firstName?.trim(), user?.lastName?.trim()]
    .filter(Boolean)
    .join(' ');

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (isSubmitting) return;

    setIsSubmitting(true);
    setSubmitError('');
    setResult('');

    try {
      const companyPhone = company?.phoneOffice
        ? await formatNum(company.phoneOffice)
        : '';

      const mobilePhone = company?.phoneMobile
        ? await formatNum(company.phoneMobile)
        : '';

      const newCompany = {
        ...company,
        phoneOffice: companyPhone,
        phoneMobile: mobilePhone,
        address: hasAddress
          ? [
              {
                street: billingAddress?.street?.trim() || '',
                city: billingAddress?.city?.trim() || '',
                province: billingAddress?.province?.trim() || '',
                postalCode: billingAddress?.postalCode?.trim() || '',
              },
            ]
          : [],
      };

      delete newCompany.trainingSub;
      delete newCompany.formsSub;

      const res = await httpCreateCompany(newCompany, user);
      const data = await res.json();

      if (!res.ok) {
        throw new Error(data?.msg || 'Unable to create your company account.');
      }

      setResult(data?.msg || 'Your company account was created successfully!');

      setIsOpen(true);
    } catch (err) {
      console.error(err);

      const message =
        err?.message ||
        'There was an error creating your company account. Please try again.';

      setSubmitError(message);
      setResult(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ReviewContainer>
      <ReviewHeader>
        <ReviewHeaderIcon>
          <FaCheck />
        </ReviewHeaderIcon>

        <ReviewHeaderContent>
          <span>Final Step</span>

          <h2>Review Your Company Account</h2>

          <p>
            Take a quick look over your information before creating your company
            account. You can go back and make changes to any section.
          </p>
        </ReviewHeaderContent>
      </ReviewHeader>

      <ReviewIntro>
        <FaUserShield />

        <div>
          <strong>Almost there</strong>

          <p>
            Creating the company will connect your manager account to the new
            company. Additional employees and company details can be added after
            setup.
          </p>
        </div>
      </ReviewIntro>

      <ReviewSections>
        <ReviewSection>
          <ReviewSectionHeader>
            <ReviewSectionTitle>
              <FaUserShield />

              <div>
                <span>Manager Account</span>
                <h3>Your Account</h3>
              </div>
            </ReviewSectionTitle>

            <ReviewEditButton type="button" onClick={() => setStep(0)}>
              Edit
            </ReviewEditButton>
          </ReviewSectionHeader>

          <ReviewDetails>
            <ReviewDetail>
              <ReviewDetailIcon>
                <FaUser />
              </ReviewDetailIcon>

              <ReviewDetailContent>
                <span>Name</span>
                <strong>{fullManagerName || 'Not provided'}</strong>
              </ReviewDetailContent>
            </ReviewDetail>

            <ReviewDetail>
              <ReviewDetailIcon>
                <FaEnvelope />
              </ReviewDetailIcon>

              <ReviewDetailContent>
                <span>Email</span>
                <strong>{user?.email || 'Not provided'}</strong>
              </ReviewDetailContent>
            </ReviewDetail>

            {user?.phoneNumber && (
              <ReviewDetail>
                <ReviewDetailIcon>
                  <FaPhone />
                </ReviewDetailIcon>

                <ReviewDetailContent>
                  <span>Phone</span>
                  <strong>{user.phoneNumber}</strong>
                </ReviewDetailContent>
              </ReviewDetail>
            )}
          </ReviewDetails>
        </ReviewSection>

        <ReviewSection>
          <ReviewSectionHeader>
            <ReviewSectionTitle>
              <FaBuilding />

              <div>
                <span>Company Details</span>
                <h3>Company Information</h3>
              </div>
            </ReviewSectionTitle>

            <ReviewEditButton type="button" onClick={() => setStep(1)}>
              Edit
            </ReviewEditButton>
          </ReviewSectionHeader>

          <ReviewDetails>
            <ReviewDetail>
              <ReviewDetailIcon>
                <FaBuilding />
              </ReviewDetailIcon>

              <ReviewDetailContent>
                <span>Company</span>
                <strong>{company?.companyName || 'Not provided'}</strong>
              </ReviewDetailContent>
            </ReviewDetail>

            <ReviewDetail>
              <ReviewDetailIcon>
                <FaEnvelope />
              </ReviewDetailIcon>

              <ReviewDetailContent>
                <span>Company Email</span>
                <strong>{company?.email || 'Not provided'}</strong>
              </ReviewDetailContent>
            </ReviewDetail>

            <ReviewDetail>
              <ReviewDetailIcon>
                <FaPhone />
              </ReviewDetailIcon>

              <ReviewDetailContent>
                <span>Office Phone</span>
                <strong>{company?.phoneOffice || 'Not provided'}</strong>
              </ReviewDetailContent>
            </ReviewDetail>

            {company?.industry && (
              <ReviewDetail>
                <ReviewDetailIcon>
                  <FaBuilding />
                </ReviewDetailIcon>

                <ReviewDetailContent>
                  <span>Industry</span>
                  <strong>{company.industry}</strong>
                </ReviewDetailContent>
              </ReviewDetail>
            )}

            {company?.website && (
              <ReviewDetail>
                <ReviewDetailIcon>
                  <FaGlobe />
                </ReviewDetailIcon>

                <ReviewDetailContent>
                  <span>Website</span>
                  <strong>{company.website}</strong>
                </ReviewDetailContent>
              </ReviewDetail>
            )}

            {company?.mainContact && (
              <ReviewDetail>
                <ReviewDetailIcon>
                  <FaUser />
                </ReviewDetailIcon>

                <ReviewDetailContent>
                  <span>Primary Contact</span>
                  <strong>{company.mainContact}</strong>
                </ReviewDetailContent>
              </ReviewDetail>
            )}
          </ReviewDetails>
        </ReviewSection>

        <ReviewSection>
          <ReviewSectionHeader>
            <ReviewSectionTitle>
              <FaMapMarkerAlt />

              <div>
                <span>Location</span>
                <h3>Company Address</h3>
              </div>
            </ReviewSectionTitle>

            <ReviewEditButton type="button" onClick={() => setStep(2)}>
              Edit
            </ReviewEditButton>
          </ReviewSectionHeader>

          {hasAddress ? (
            <ReviewDetails>
              <ReviewDetail>
                <ReviewDetailIcon>
                  <FaMapMarkerAlt />
                </ReviewDetailIcon>

                <ReviewDetailContent>
                  <span>Address</span>

                  <strong>
                    {billingAddress?.street}
                    <br />
                    {billingAddress?.city}
                    {billingAddress?.province
                      ? `, ${billingAddress.province}`
                      : ''}
                    {billingAddress?.postalCode
                      ? ` ${billingAddress.postalCode}`
                      : ''}
                  </strong>
                </ReviewDetailContent>
              </ReviewDetail>
            </ReviewDetails>
          ) : (
            <ReviewEmpty>
              <FaMapMarkerAlt />

              <div>
                <strong>No address added</strong>

                <p>
                  That&apos;s okay. You can add your company address later from
                  your company profile.
                </p>
              </div>
            </ReviewEmpty>
          )}
        </ReviewSection>
      </ReviewSections>

      <ReviewConfirmation>
        <ReviewConfirmationIcon>
          <FaCheck />
        </ReviewConfirmationIcon>

        <ReviewConfirmationContent>
          <strong>Ready to create your company?</strong>

          <p>
            Your manager account and company will be created using the
            information above. You&apos;ll be able to continue setting up your
            company afterward.
          </p>
        </ReviewConfirmationContent>
      </ReviewConfirmation>

      {submitError && <ReviewError role="alert">{submitError}</ReviewError>}

      <ReviewActions>
        <ReviewBackButton
          type="button"
          onClick={() => setStep(2)}
          disabled={isSubmitting}
        >
          Back
        </ReviewBackButton>

        <PrimaryButtonComponent
          functionName={handleSubmit}
          span={isSubmitting ? 'Creating Company...' : 'Create Company Account'}
          disabled={isSubmitting}
        />
      </ReviewActions>
    </ReviewContainer>
  );
};

CompanySignupReview.propTypes = {
  company: PropTypes.object,
  user: PropTypes.object,
  billingAddress: PropTypes.object,
  setStep: PropTypes.func,
  setResult: PropTypes.func,
  setIsOpen: PropTypes.func,
};

export default CompanySignupReview;
